type Row = (label: string, value: string, cls?: string) => string;
type ProjectProgress = { progress?: unknown; progressState?: unknown; stage?: unknown; status?: unknown };
const stages: Record<string, string> = {
  construction: '施工中', acceptance: '待验收', reinforcement: '待加固复验', fitout: '待装修',
  delivered: '已交付', cancelled: '已取消', collapsed: '已倒塌', unassigned: '待承包',
};
const record = (v: unknown): Record<string, unknown> | null => v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
const text = (v: unknown): string => typeof v === 'string' ? v.trim() : '';

/** 工程计量的百分比；交付是另一个状态，不能由舍入后的100%推断。 */
export function projectProgressPercent(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) return '未记录';
  if (value === 1) return '100%';
  if (value === 0) return '0%';
  const p = value * 100;
  if (p > 99.9) return '>99.9%';
  if (p < 0.1) return '<0.1%';
  return `${Math.min(99.9, Math.round(p * 10) / 10)}%`;
}

export function projectProgressStage(value: unknown): string {
  const p = record(value) as ProjectProgress | null;
  if (!p) return '阶段未记录';
  const state = record(p.progressState), phase = text(state?.phase);
  const phaseStage = Object.hasOwn(stages, phase) ? stages[phase] : '';
  if (['delivered', 'cancelled', 'collapsed'].includes(phase)) return stages[phase];
  // 后端给出实际工序/验收/复验阶段；旧引擎仍显示原stage/status，不猜成已交付。
  return text(state?.stage) || phaseStage || text(p.stage) || text(p.status) ||
    (p.progress === 1 ? '交付状态未记录' : '阶段未记录');
}

/** 无独立阶段列的简表也必须交代验收/装修，不能只有一个100%。调用方照常转义。 */
export function projectProgressText(value: unknown): string {
  const p = record(value);
  return `${projectProgressPercent(p?.progress)} · ${projectProgressStage(value)}`;
}

/** 仅显示当前装修观察；余额不是提款承诺，当前邻城供给也不是停工的唯一原因。 */
export function renderProjectDelivery(value: unknown, row: Row, money: (v: number) => string, date: (v: number) => string): string | null {
  const d = record(value);
  const keys = ['asOf', 'wanted', 'got', 'left', 'paid', 'remainingEstimate', 'liquidAssets', 'supplyToday'] as const;
  if (!d || !keys.every(k => typeof d[k] === 'number' && Number.isFinite(d[k]) && (d[k] as number) >= 0) ||
      !Number.isInteger(d.asOf) || typeof d.ready !== 'boolean') return null;
  const n = d as Record<typeof keys[number], number> & { ready: boolean };
  const qty = (v: number) => v > 0 && v < 0.001 ? '&lt;0.001' : v.toLocaleString('en-US', { maximumFractionDigits: 3 });
  return row('装修设备', `已购 ${qty(n.got)} / 需 ${qty(n.wanted)} 份`) +
    row('尚缺装修设备', `${qty(n.left)} 份${n.ready ? ' · 设备已备齐，待交付流程' : ''}`) +
    row('剩余购置估价', money(n.remainingEstimate)) +
    `<details><summary>当前资金与供应</summary>${row('观察日', date(n.asOf))}${row('装修设备已实付', money(n.paid))}` +
    row('投资人现金与存款', `<span title="当前余额，不保证能当场提款">${money(n.liquidAssets)}</span>`) +
    row('邻城当前可供', `${qty(n.supplyToday)} 份`) + '</details>';
}
